// cookieConsent.js
// Manages GDPR cookie consent preferences in localStorage.
// Consent expires after 30 days or when the consent version changes.

const CONSENT_KEY = 'holi_cookie_consent';
const CONSENT_EXPIRY_DAYS = 30;

// Increment this version whenever the cookie policy / categories change.
// This will invalidate all existing consents and force users to re-consent.
export const CONSENT_VERSION = '1.0';

/**
 * Categories of cookies/tracking:
 *  - necessary:   Always enabled. Required for checkout, cart, sessions.
 *  - analytics:   Optional. Google Analytics / performance tracking.
 *  - marketing:   Optional. Conversion tracking for visitors arriving via Instagram / TikTok ads.
 */
export const CONSENT_CATEGORIES = {
  necessary: {
    key: 'necessary',
    label: 'Notwendig',
    description:
      'Diese Cookies sind für die Grundfunktionen der Website erforderlich, z.\u00A0B. für den Warenkorb und den Bestellvorgang. Sie können nicht deaktiviert werden.',
    required: true,
  },
  analytics: {
    key: 'analytics',
    label: 'Statistiken & Leistung',
    description:
      'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren. Die Daten werden anonymisiert erfasst.',
    required: false,
  },
  marketing: {
    key: 'marketing',
    label: 'Marketing & Conversion-Tracking',
    description:
      'Diese Cookies ermöglichen es uns nachzuvollziehen, ob Besucher über eine von uns genutzten Werbeplattform auf unsere Website gelangt sind. So können wir den Erfolg unserer Kampagnen messen und verbessern.',
    required: false,
  },
};

/**
 * Returns the default consent object (only necessary enabled).
 */
export function getDefaultConsent() {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
  };
}

/**
 * Saves consent preferences to localStorage.
 * @param {{ necessary: boolean, analytics: boolean, marketing: boolean }} preferences
 */
export function saveConsent(preferences) {
  const data = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    preferences: {
      ...preferences,
      necessary: true, // always true
    },
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
}

/**
 * Loads consent preferences from localStorage.
 * Returns null if no consent stored, expired, or version mismatch.
 * @returns {{ necessary: boolean, analytics: boolean, marketing: boolean } | null}
 */
export function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Version mismatch → policy changed → re-consent required
    if (data.version !== CONSENT_VERSION) {
      clearConsent();
      return null;
    }

    // Check expiry (30 days)
    const storedDate = new Date(data.timestamp);
    const now = new Date();
    const diffDays = (now - storedDate) / (1000 * 60 * 60 * 24);
    if (diffDays > CONSENT_EXPIRY_DAYS) {
      clearConsent();
      return null;
    }

    return data.preferences;
  } catch {
    clearConsent();
    return null;
  }
}

/**
 * Removes stored consent.
 */
export function clearConsent() {
  localStorage.removeItem(CONSENT_KEY);
}

/**
 * Returns true if consent has been given (valid & not expired).
 */
export function hasValidConsent() {
  return loadConsent() !== null;
}
